// Inline achievements gallery for Firefly / Activity surfaces.
// Collapsed (default): recent badge graphics + names only.
// Expanded: dates, refresh, revoke.

import { useState } from "react";
import { AdsConfirmDialog } from "@/components/ads-assistant/AdsConfirmDialog";
import { AchievementBadge } from "@/components/common/kpi/AchievementBadge";
import { useAuth } from "@/contexts/AuthContext";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { useUserAchievements } from "@/hooks/useUserAchievements";
import { canEditAdsGuidance } from "@/lib/adsAssistantPermissions";
import { filterAchievementsForBucket } from "@/lib/achievementBucketFilter";
import type { BucketId } from "@/components/shell/modes/bucketsRegistry";

function formatUnlockedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

const COLLAPSED_BADGE_LIMIT = 6;

export function ActivityPanelAchievements({
  bucketId = null,
}: {
  bucketId?: BucketId | null;
} = {}) {
  const auth = useAuth();
  const scopedOrgId = useScopedOrganizationId();
  const canRevoke = canEditAdsGuidance(auth);
  const [pendingRevokeId, setPendingRevokeId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const vm = useUserAchievements({ enabled: true, scopedOrgId });
  const visibleAchievements = filterAchievementsForBucket(
    vm.achievements,
    bucketId,
  );
  const collapsedBadges = visibleAchievements.slice(0, COLLAPSED_BADGE_LIMIT);

  return (
    <section
      className="rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm dark:border-gray-700 dark:bg-zinc-900"
      aria-labelledby="activity-achievements-heading"
    >
      <div className="flex items-center justify-between gap-2">
        <h3
          id="activity-achievements-heading"
          className="text-sm font-semibold text-gray-900 dark:text-gray-100"
        >
          Achievements
        </h3>
        <div className="flex shrink-0 items-center gap-2">
          {expanded ? (
            <button
              type="button"
              onClick={() => void vm.refresh()}
              disabled={vm.loading}
              className="text-xs font-medium text-blue-700 hover:underline disabled:opacity-50 dark:text-blue-300"
            >
              Refresh
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs font-medium text-gray-600 hover:underline dark:text-gray-300"
            aria-expanded={expanded}
          >
            {expanded ? "Collapse" : "Expand"}
          </button>
        </div>
      </div>

      {vm.error ? (
        <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-900 dark:bg-amber-950/30">
          {vm.error}
        </p>
      ) : null}

      {vm.loading && !visibleAchievements.length ? (
        <p className="mt-2 text-sm text-gray-500">Loading…</p>
      ) : null}

      {!vm.loading && !vm.error && visibleAchievements.length === 0 ? (
        <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
          {vm.achievements.length > 0 ? (
            <>No achievements for this view yet.</>
          ) : (
            <>No achievements yet — log ads and complete training to earn badges.</>
          )}
        </p>
      ) : null}

      {!expanded && collapsedBadges.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-2">
          {collapsedBadges.map((a) => (
            <li key={a.id} className="min-w-0">
              <AchievementBadge achievementKey={a.achievement_key} metadata={a.metadata} />
            </li>
          ))}
          {visibleAchievements.length > COLLAPSED_BADGE_LIMIT ? (
            <li className="flex items-center text-[11px] text-gray-500 dark:text-gray-400">
              +{visibleAchievements.length - COLLAPSED_BADGE_LIMIT} more
            </li>
          ) : null}
        </ul>
      ) : null}

      {expanded && visibleAchievements.length > 0 ? (
        <ul className="mt-2 grid grid-cols-1 gap-2">
          {visibleAchievements.map((a) => {
            const periodKey = (a.metadata as Record<string, unknown> | undefined)?.period_key;
            return (
              <li
                key={a.id}
                className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2 dark:border-gray-600 dark:bg-zinc-800/50"
              >
                <div className="min-w-0 space-y-1">
                  <AchievementBadge achievementKey={a.achievement_key} metadata={a.metadata} />
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    {formatUnlockedAt(a.unlocked_at)}
                    {typeof periodKey === "string" && periodKey ? ` · ${periodKey}` : ""}
                  </p>
                </div>
                {canRevoke ? (
                  <button
                    type="button"
                    onClick={() => setPendingRevokeId(a.id)}
                    disabled={vm.revokingId === a.id}
                    className="shrink-0 text-[11px] font-medium text-red-700 hover:underline disabled:opacity-50 dark:text-red-300"
                  >
                    Revoke
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      <AdsConfirmDialog
        open={pendingRevokeId !== null}
        title="Revoke achievement?"
        message="This removes the badge from the user's gallery. They may earn it again if the activity qualifies."
        confirmLabel="Revoke"
        cancelLabel="Cancel"
        danger
        onCancel={() => setPendingRevokeId(null)}
        onConfirm={() => {
          const id = pendingRevokeId;
          setPendingRevokeId(null);
          if (id) void vm.revoke(id);
        }}
      />
    </section>
  );
}
